import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';
import { useApp } from '../../context/AppContext';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import useHandleErrors from '../../hooks/useHandleErrors';
import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    FormControlLabel,
    InputAdornment,
    IconButton,
    Link,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import InputFile from '../../components/InputFile';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import logo from '../../assets/logo.png';
import { LandingNavbar } from '../Welcome';
import { noAutoFormat } from "../../global/helpers";
import DateInput from '../../components/DateInput/Index';
import { IDADE_MINIMA_CONVIVA, calcularIdade, precisaDeResponsavel } from '../../global/idade';

const RegisterSchema = Yup.object().shape({
    name: Yup.string().required('Campo obrigatório').max(50, 'Use no máximo 50 caracteres'),
    username: Yup.string().matches(/^[0-9a-z_.]*$/, 'Usuário inválido').required('Campo obrigatório'),
    email: Yup.string().email('E-mail inválido').matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, 'E-mail inválido').required('Campo obrigatório'),
    phone: Yup.string()
        .required('Campo obrigatório')
        .matches(/^\(\d{2}\) \d{5}-\d{4}$/, 'Telefone inválido. Use o formato (XX) XXXXX-XXXX'),
    password: Yup.string().min(6, 'Mínimo de 6 caracteres').required('Campo obrigatório'),
    repeat_password: Yup.string()
        .oneOf([Yup.ref('password'), null], 'As senhas não conferem')
        .required('Campo obrigatório'),
    // A idade define o resto: abaixo de 13 nao ha cadastro (Termos 2.2), e de
    // 13 a 18 o cadastro so vale com autorizacao dos pais
    birthdate: Yup.string()
        .required('Campo obrigatório')
        .test('idade-minima', `A Empiria aceita cadastros a partir de ${IDADE_MINIMA_CONVIVA} anos`, valor => {
            const idade = calcularIdade(valor);
            return idade === null || idade >= IDADE_MINIMA_CONVIVA;
        }),
    guardian_name: Yup.string().when('birthdate', {
        is: valor => precisaDeResponsavel(valor),
        then: schema => schema.required('Campo obrigatório'),
    }),
    guardian_email: Yup.string()
        .email('E-mail inválido')
        .when('birthdate', {
            is: valor => precisaDeResponsavel(valor),
            then: schema => schema.required('Campo obrigatório'),
        }),
    terms: Yup.boolean().required('Você precisa aceitar os termos'),
});

const Register = () => {
    const [search] = useSearchParams();
    const { Login } = useAuth();
    const { isOffline } = useApp();
    const [hasError, setError] = useState();
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const [convivaThanks, setConvivaThanks] = useState(null);
    // Cadastro de adolescente: nao ha login automatico, so o aviso ao responsavel
    const [aguardandoResponsavel, setAguardandoResponsavel] = useState(null);
    useHandleErrors(hasError);

    const navigation = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const {
        values, errors, touched, handleChange, handleBlur,
        handleSubmit, isSubmitting, setFieldValue,
    } = useFormik({
        initialValues: { email: '', name: '', password: '', username: '', phone: '', birthdate: '', guardian_name: '', guardian_email: '', terms: false, profile_image: null },
        validationSchema: RegisterSchema,
        validateOnBlur: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                const { data: cadastro } = await server.post('/auth/signup', values);

                // Adolescente de 13 a 17: a conta existe, mas nao entra enquanto
                // o responsavel nao confirmar. Tentar logar aqui so daria erro.
                if (cadastro?.data?.awaiting_guardian_consent) {
                    setAguardandoResponsavel(cadastro.message);
                    setSubmitting(false);
                    return;
                }

                enqueueSnackbar('Conta criada com sucesso!', { variant: 'success' });

                const { data } = await server.post('auth', values);
                Login(data.data);
                setSubmitting(false);

                const sign = search.get('sign');
                if (sign === 'encontrado') {
                    // Cadastro iniciado como anfitriao: a conta de conviva e apenas o primeiro
                    // passo, entao nao agradecemos aqui. O agradecimento vem no fim do cadastro
                    // de anfitriao, em Profile/Receiver.
                    navigation('/perfil?sign=encontrado');
                } else {
                    const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
                    sessionStorage.removeItem('redirectAfterLogin');
                    // Cadastro de conviva concluido: agradece antes de ir para a area logada
                    setConvivaThanks(redirectUrl || '/lista');
                }
            } catch (error) {
                setSubmitting(false);
                const msg = error.response?.data?.message || error.message || 'Erro desconhecido';
                enqueueSnackbar(msg, { variant: 'error' });
            }
        },
    });

    const closeConvivaThanks = () => {
        const destination = convivaThanks;
        setConvivaThanks(null);
        navigation(destination || '/lista');
    };

    return (
        <>
            <LandingNavbar />
            <Box sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                bgcolor: '#f5f5f5',
                p: { xs: '80px 12px 32px', sm: '90px 16px 48px' },
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: 480,
                    gap: 3,
                }}>
                    {/* Card do formulario */}
                    <Box sx={{
                        width: '100%',
                        bgcolor: 'white',
                        borderRadius: '16px',
                        border: '1px solid #e8e8e8',
                        p: { xs: '24px 20px', sm: '32px 28px' },
                    }}>
                        {/* Header */}
                        <Stack spacing={0.5} alignItems="center" sx={{ mb: 3 }}>
                            <Box
                                component="img"
                                src={logo}
                                sx={{ width: { xs: 100, sm: 120 }, mb: 1 }}
                            />
                            <Typography variant="h6" fontWeight={600} color="#333">
                                Criar sua conta
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Preencha os dados para se cadastrar
                            </Typography>
                        </Stack>

                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2}>
                                {/* Foto de perfil */}
                                <Stack alignItems="center" spacing={1}>
                                    <Avatar
                                        src={values.preview_image}
                                        sx={{ width: 80, height: 80, border: '2px solid #e0e0e0' }}
                                    />
                                    <InputFile
                                        onChange={e => {
                                            setFieldValue('profile_image', e.base64);
                                            setFieldValue('preview_image', e.preview);
                                        }}
                                        name="image"
                                    />
                                </Stack>

                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="Seu nome de usuário"
                                    InputLabelProps={{ shrink: true }}
                                    name="username"
                                    value={values.username}
                                    onChange={e => setFieldValue('username', e.target.value.toLowerCase())}
                                    onBlur={handleBlur}
                                    disabled={isOffline}
                                    error={Boolean(errors.username)}
                                    helperText={errors.username || 'Você pode usar letras minusculas e números.'}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">@</InputAdornment>,
                                        endAdornment: <InputAdornment position="end">{values.username.length}</InputAdornment>,
                                    }}
                                    inputProps={{ ...noAutoFormat, maxLength: 20 }}
                                />

                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="Nome completo"
                                    InputLabelProps={{ shrink: true }}
                                    name="name"
                                    value={values.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isOffline}
                                    error={Boolean(errors.name)}
                                    helperText={errors.name}
                                    inputProps={{ ...noAutoFormat, maxLength: 50 }}
                                />

                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="E-mail"
                                    InputLabelProps={{ shrink: true }}
                                    name="email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isOffline}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email}
                                />

                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="Telefone (WhatsApp)"
                                    InputLabelProps={{ shrink: true }}
                                    name="phone"
                                    placeholder="(XX) XXXXX-XXXX"
                                    value={values.phone}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/\D/g, '');
                                        if (value.length <= 11) {
                                            value = value.replace(/^(\d{2})(\d)/g, '($1) $2');
                                            value = value.replace(/(\d{5})(\d)/, '$1-$2');
                                        }
                                        setFieldValue('phone', value);
                                    }}
                                    onBlur={handleBlur}
                                    disabled={isOffline}
                                    error={Boolean(errors.phone)}
                                    helperText={errors.phone || 'Número para receber notificações via WhatsApp'}
                                    inputProps={{ maxLength: 15 }}
                                />

                                {/* A data de nascimento substitui a antiga declaracao de
                                    maioridade: sem ela nao ha como aplicar o minimo de 13
                                    anos nem exigir 18 de quem vira anfitriao. */}
                                <DateInput
                                    label="Data de nascimento"
                                    name="birthdate"
                                    obrigatorio
                                    naoFuturo
                                    value={values.birthdate}
                                    onChange={valor => setFieldValue('birthdate', valor)}
                                    onBlur={handleBlur}
                                    disabled={isOffline}
                                    erro={Boolean(errors.birthdate)}
                                    textoAuxiliar={errors.birthdate}
                                />

                                {precisaDeResponsavel(values.birthdate) && (
                                    <Stack spacing={2} sx={{ bgcolor: '#f7fbfa', border: '1px solid #cfe5e2', borderRadius: '12px', p: 2 }}>
                                        <Typography sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.secondary' }}>
                                            Você tem {calcularIdade(values.birthdate)} anos, então o cadastro
                                            precisa da autorização de um responsável. Enviaremos um e-mail para
                                            ele confirmar — até lá, a conta não pode ser usada.
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            size="small"
                                            required
                                            label="Nome do responsável"
                                            InputLabelProps={{ shrink: true }}
                                            name="guardian_name"
                                            value={values.guardian_name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={isOffline}
                                            error={Boolean(errors.guardian_name)}
                                            helperText={errors.guardian_name}
                                        />

                                        <TextField
                                            fullWidth
                                            size="small"
                                            required
                                            label="E-mail do responsável"
                                            InputLabelProps={{ shrink: true }}
                                            name="guardian_email"
                                            value={values.guardian_email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={isOffline}
                                            error={Boolean(errors.guardian_email)}
                                            helperText={errors.guardian_email || 'É para cá que o pedido de autorização vai'}
                                            {...noAutoFormat}
                                        />
                                    </Stack>
                                )}

                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="Senha"
                                    InputLabelProps={{ shrink: true }}
                                    name="password"
                                    inputProps={noAutoFormat}
                                    type={showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isOffline}
                                    error={Boolean(errors.password)}
                                    helperText={errors.password}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end" size="small">
                                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    size="small"
                                    required
                                    label="Repita a senha"
                                    InputLabelProps={{ shrink: true }}
                                    name="repeat_password"
                                    inputProps={noAutoFormat}
                                    type={showRepeatPassword ? 'text' : 'password'}
                                    value={values.repeat_password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={isOffline}
                                    error={Boolean(errors.repeat_password)}
                                    helperText={errors.repeat_password}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowRepeatPassword(prev => !prev)} edge="end" size="small">
                                                    {showRepeatPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <FormControlLabel
                                    sx={{ alignItems: 'flex-start', m: 0 }}
                                    control={
                                        <Checkbox
                                            name="terms"
                                            checked={values.terms}
                                            onChange={(e, value) => setFieldValue('terms', value)}
                                            sx={{ pt: 0, '&.Mui-checked': { color: '#006a61' } }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ fontSize: 12, lineHeight: 1.4, color: 'text.secondary' }}>
                                            Ao clicar aqui, (i) concordo com os <a href="/politicas" style={{ color: '#006a61' }}>Termos de Uso e Políticas</a> e (ii) tenho ciência da <a href="/privacidade" style={{ color: '#006a61' }}>Política de Privacidade</a> da Empiria.
                                        </Typography>
                                    }
                                />

                                <LoadingButton
                                    type="submit"
                                    fullWidth
                                    loading={isSubmitting}
                                    variant="contained"
                                    disabled={Boolean(Object.keys(errors).length) || isSubmitting}
                                    sx={{
                                        bgcolor: '#006a61',
                                        borderRadius: '8px',
                                        height: 44,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        '&:hover': { bgcolor: '#005a52' },
                                    }}
                                >
                                    Cadastrar
                                </LoadingButton>

                                <Typography variant="body2" color="text.secondary" textAlign="center">
                                    Ja possui uma conta?{' '}
                                    <Link
                                        component="button"
                                        type="button"
                                        variant="body2"
                                        onClick={() => navigation('/login')}
                                        sx={{ cursor: 'pointer', color: '#006a61', fontWeight: 500 }}
                                    >
                                        Faça login.
                                    </Link>
                                </Typography>
                            </Stack>
                        </form>
                    </Box>

                    {/* Voltar */}
                    <Typography
                        variant="body2"
                        onClick={() => navigation('/')}
                        sx={{
                            color: '#006a61',
                            cursor: 'pointer',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Voltar para a página inicial
                    </Typography>

                    {/* Footer */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, mb: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                            <Link href="/chat-bot" sx={{ color: 'text.secondary' }}>Suporte</Link>
                            {' - '}
                            <Link href="/politicas" sx={{ color: 'text.secondary' }}>Termos e Políticas</Link>
                            {' - '}
                            Versão {localStorage.getItem('current_version')}
                        </Typography>
                    </Stack>
                </Box>
            </Box>

            {/* Cadastro de 13 a 17 anos: nada de login, o proximo passo e do responsavel */}
            <Dialog
                open={Boolean(aguardandoResponsavel)}
                onClose={() => navigation('/login')}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: 420 } }}
            >
                <DialogContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
                    <MarkEmailReadOutlinedIcon sx={{ fontSize: 64, color: '#006a61', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Falta a autorização do responsável
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {aguardandoResponsavel}
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => navigation('/login')}
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        Entendi
                    </Button>
                </DialogContent>
            </Dialog>

            {/* Agradecimento por se tornar conviva */}
            <Dialog
                open={Boolean(convivaThanks)}
                onClose={closeConvivaThanks}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: 420 } }}
            >
                <DialogContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Obrigado por se tornar um conviva!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        Sua conta foi criada com sucesso. Agora você ja pode explorar os encontros
                        disponíveis e agendar o seu primeiro. Bons encontros!
                    </Typography>
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={closeConvivaThanks}
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        Continuar
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Register;
